import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { LogOut, Save, RotateCcw, ExternalLink, Inbox } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

import EditBrand from "../components/admin/EditBrand";
import EditWhatsapp from "../components/admin/EditWhatsapp";
import EditCta from "../components/admin/EditCta";
import EditVisibility from "../components/admin/EditVisibility";
import EditSeo from "../components/admin/EditSeo";
import EditTracking from "../components/admin/EditTracking";
import EditHero from "../components/admin/EditHero";
import EditStats from "../components/admin/EditStats";
import EditAudience from "../components/admin/EditAudience";
import EditMethod from "../components/admin/EditMethod";
import EditBmi from "../components/admin/EditBmi";
import EditPhotoGallery from "../components/admin/EditPhotoGallery";
import EditVideoGallery from "../components/admin/EditVideoGallery";
import EditBeforeAfter from "../components/admin/EditBeforeAfter";
import EditTestimonials from "../components/admin/EditTestimonials";
import EditIncluded from "../components/admin/EditIncluded";
import EditBonus from "../components/admin/EditBonus";
import EditPricing from "../components/admin/EditPricing";
import EditAbout from "../components/admin/EditAbout";
import EditLeadCapture from "../components/admin/EditLeadCapture";
import EditGuarantee from "../components/admin/EditGuarantee";
import EditFAQ from "../components/admin/EditFAQ";
import EditExternalLinks from "../components/admin/EditExternalLinks";
import EditStickyCta from "../components/admin/EditStickyCta";
import EditCookieConsent from "../components/admin/EditCookieConsent";
import EditFooter from "../components/admin/EditFooter";
import EditSettings from "../components/admin/EditSettings";

const TABS = [
  // Essentials (most important first)
  { value: "visibility", label: "Visibilidade", group: "config" },
  { value: "cta", label: "Página Vendas", group: "config" },
  { value: "brand", label: "Marca", group: "config" },
  { value: "whatsapp", label: "WhatsApp", group: "config" },
  { value: "seo", label: "SEO", group: "config" },
  { value: "tracking", label: "Pixels", group: "config" },
  { value: "settings", label: "Conta", group: "config" },
  // Sections
  { value: "hero", label: "Hero", group: "section" },
  { value: "stats", label: "Stats", group: "section" },
  { value: "audience", label: "Público", group: "section" },
  { value: "bmiCalculator", label: "IMC", group: "section" },
  { value: "method", label: "Método", group: "section" },
  { value: "photoGallery", label: "Fotos", group: "section" },
  { value: "videoGallery", label: "Vídeos", group: "section" },
  { value: "beforeAfter", label: "Antes/Depois", group: "section" },
  { value: "testimonials", label: "Depoimentos", group: "section" },
  { value: "included", label: "Inclui", group: "section" },
  { value: "bonus", label: "Bônus", group: "section" },
  { value: "pricing", label: "Investimento", group: "section" },
  { value: "about", label: "Sobre", group: "section" },
  { value: "leadCapture", label: "Form Leads", group: "section" },
  { value: "guarantee", label: "Garantia", group: "section" },
  { value: "faq", label: "FAQ", group: "section" },
  { value: "externalLinks", label: "Links Externos", group: "section" },
  { value: "stickyCta", label: "Barra CTA", group: "section" },
  { value: "cookieConsent", label: "Cookies", group: "section" },
  { value: "footer", label: "Footer", group: "section" },
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/content")
      .then((res) => setContent(res.data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message))
      .finally(() => setLoading(false));
  }, []);

  const updateSection = (key, value) => setContent((c) => ({ ...c, [key]: value }));

  const onSave = async () => {
    setSaving(true);
    try {
      await api.put("/content", content);
      toast.success("Conteúdo salvo com sucesso!");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const onReset = async () => {
    if (!window.confirm("Tem certeza? Isso vai restaurar o conteúdo padrão e descartar suas alterações.")) return;
    setSaving(true);
    try {
      const { data } = await api.post("/content/reset");
      setContent(data);
      toast.success("Conteúdo restaurado para o padrão.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const onLogout = () => { logout(); nav("/admin/login"); };

  if (loading || !content) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-alabaster">
        <div data-testid="admin-loading" className="font-serif text-2xl">Carregando…</div>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-alabaster">
      <header className="sticky top-0 z-30 bg-onyx text-alabaster">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="font-serif text-xl">{content.brand?.name || "Painel Admin"}</span>
            <span className="text-xs tracking-[0.2em] uppercase text-gold border-l border-gold/30 pl-3">CMS</span>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/admin/leads"
              data-testid="admin-leads-link"
              className="inline-flex items-center gap-2 px-3 py-2 text-xs tracking-[0.2em] uppercase border border-alabaster/30 hover:bg-alabaster/10 transition-colors"
            >
              <Inbox size={14} /> <span className="hidden sm:inline">Leads</span>
            </Link>
            <a href="/" target="_blank" rel="noreferrer" data-testid="admin-preview-site" className="hidden sm:inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase hover:text-gold transition-colors px-3 py-2">
              Ver site <ExternalLink size={14} />
            </a>
            <button
              data-testid="admin-reset-btn"
              onClick={onReset}
              disabled={saving}
              className="hidden sm:inline-flex items-center gap-2 px-3 py-2 text-xs tracking-[0.2em] uppercase border border-alabaster/30 hover:bg-alabaster/10 transition-colors disabled:opacity-50"
            >
              <RotateCcw size={14} /> Restaurar
            </button>
            <button
              data-testid="admin-save-btn"
              onClick={onSave}
              disabled={saving}
              className="inline-flex items-center gap-2 px-4 py-2 bg-gold text-onyx text-xs tracking-[0.2em] uppercase font-semibold hover:bg-alabaster transition-colors disabled:opacity-50"
            >
              <Save size={14} /> {saving ? "Salvando…" : "Salvar"}
            </button>
            <button data-testid="admin-logout-btn" onClick={onLogout} className="inline-flex items-center gap-2 px-3 py-2 text-xs tracking-[0.2em] uppercase hover:text-gold transition-colors" aria-label="Logout">
              <LogOut size={14} />
            </button>
          </div>
        </div>
        <p className="px-6 pb-2 text-xs text-alabaster/50">Logado como {user?.email}</p>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <Tabs defaultValue="visibility" className="w-full">
          <div className="overflow-x-auto -mx-4 px-4 mb-6">
            <TabsList className="bg-white border border-border h-auto flex-wrap justify-start gap-1 p-1">
              {TABS.map((t) => (
                <TabsTrigger
                  key={t.value}
                  value={t.value}
                  data-testid={`admin-tab-${t.value}`}
                  className={`text-xs tracking-wide data-[state=active]:bg-onyx data-[state=active]:text-alabaster ${
                    t.group === "config" ? "border-r border-border/60 pr-2" : ""
                  }`}
                >
                  {t.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>

          <TabsContent value="visibility"><EditVisibility value={content.visibility} onChange={(v) => updateSection("visibility", v)} /></TabsContent>
          <TabsContent value="cta"><EditCta value={content.cta} onChange={(v) => updateSection("cta", v)} /></TabsContent>
          <TabsContent value="brand"><EditBrand value={content.brand} onChange={(v) => updateSection("brand", v)} /></TabsContent>
          <TabsContent value="whatsapp"><EditWhatsapp value={content.whatsapp} onChange={(v) => updateSection("whatsapp", v)} /></TabsContent>
          <TabsContent value="seo"><EditSeo value={content.seo} onChange={(v) => updateSection("seo", v)} /></TabsContent>
          <TabsContent value="tracking"><EditTracking value={content.tracking} onChange={(v) => updateSection("tracking", v)} /></TabsContent>
          <TabsContent value="settings"><EditSettings /></TabsContent>
          <TabsContent value="hero"><EditHero value={content.hero} onChange={(v) => updateSection("hero", v)} /></TabsContent>
          <TabsContent value="stats"><EditStats value={content.stats} onChange={(v) => updateSection("stats", v)} /></TabsContent>
          <TabsContent value="audience"><EditAudience value={content.audience} onChange={(v) => updateSection("audience", v)} /></TabsContent>
          <TabsContent value="bmiCalculator"><EditBmi value={content.bmiCalculator} onChange={(v) => updateSection("bmiCalculator", v)} /></TabsContent>
          <TabsContent value="method"><EditMethod value={content.method} onChange={(v) => updateSection("method", v)} /></TabsContent>
          <TabsContent value="photoGallery"><EditPhotoGallery value={content.photoGallery} onChange={(v) => updateSection("photoGallery", v)} /></TabsContent>
          <TabsContent value="videoGallery"><EditVideoGallery value={content.videoGallery} onChange={(v) => updateSection("videoGallery", v)} /></TabsContent>
          <TabsContent value="beforeAfter"><EditBeforeAfter value={content.beforeAfter} onChange={(v) => updateSection("beforeAfter", v)} /></TabsContent>
          <TabsContent value="testimonials"><EditTestimonials value={content.testimonials} onChange={(v) => updateSection("testimonials", v)} /></TabsContent>
          <TabsContent value="included"><EditIncluded value={content.included} onChange={(v) => updateSection("included", v)} /></TabsContent>
          <TabsContent value="bonus"><EditBonus value={content.bonus} onChange={(v) => updateSection("bonus", v)} /></TabsContent>
          <TabsContent value="pricing"><EditPricing value={content.pricing} onChange={(v) => updateSection("pricing", v)} /></TabsContent>
          <TabsContent value="about"><EditAbout value={content.about} onChange={(v) => updateSection("about", v)} /></TabsContent>
          <TabsContent value="leadCapture"><EditLeadCapture value={content.leadCapture} onChange={(v) => updateSection("leadCapture", v)} /></TabsContent>
          <TabsContent value="guarantee"><EditGuarantee value={content.guarantee} onChange={(v) => updateSection("guarantee", v)} /></TabsContent>
          <TabsContent value="faq"><EditFAQ value={content.faq} onChange={(v) => updateSection("faq", v)} /></TabsContent>
          <TabsContent value="externalLinks"><EditExternalLinks value={content.externalLinks} onChange={(v) => updateSection("externalLinks", v)} /></TabsContent>
          <TabsContent value="stickyCta"><EditStickyCta value={content.stickyCta} onChange={(v) => updateSection("stickyCta", v)} /></TabsContent>
          <TabsContent value="cookieConsent"><EditCookieConsent value={content.cookieConsent} onChange={(v) => updateSection("cookieConsent", v)} /></TabsContent>
          <TabsContent value="footer"><EditFooter value={content.footer} onChange={(v) => updateSection("footer", v)} /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
